import type { TicketType } from '@nadoutong/contracts';
import {
  TICKET_ATTACHMENT_CONTENT_TYPES,
  TICKET_ATTACHMENT_MAX_BYTES,
  TICKET_MAX_ATTACHMENTS,
  TICKET_TEXT_MAX_LENGTH,
  TICKET_TYPES,
} from '@nadoutong/contracts';
import { ImagePlus, Paperclip, Send, X } from 'lucide-react';
import { useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useCreateTicket } from '../../api/useTickets.ts';
import { useToast } from '../../components/Toast.tsx';
import { useSession } from '../../hooks/useSession.tsx';
import { useT } from '../../i18n/index.ts';
import { Button, FormField, Input, Panel, Textarea, cx } from '../../ui/index.ts';
import { TICKET_TYPE_LABEL_KEYS, describeCreateTicketError } from './ticketMeta.ts';

/**
 * Ticket intake form (tenant #116). Client-side pre-validation mirrors
 * what the server re-verifies: description trimmed 1..500, at most 3
 * image attachments (png/jpeg/webp/gif, ≤2 MB each), `dataBase64` as
 * RAW base64 with the `data:` URL prefix stripped.
 *
 * The email field is display-only: the contract treats a null/absent/
 * empty `email` as "fall back to the session account's email", which is
 * exactly the address shown, so the payload simply omits it.
 */

// Same native-select chrome `/requests` uses — native on purpose, the
// design-system Radix select stays for filter rows that need it.
const SELECT_CLASS =
  'h-9 w-full rounded border border-line bg-raised px-3 text-body text-ink transition-colors duration-150 hover:border-line-strong focus:border-forest focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/30 disabled:pointer-events-none disabled:opacity-60';

interface PendingAttachment {
  readonly id: string;
  readonly filename: string;
  readonly contentType: string;
  /** Raw base64, `data:` prefix already stripped. */
  readonly dataBase64: string;
}

/** `data:image/png;base64,AAAA` → `AAAA`. Exported for the unit test. */
export function stripDataUrlPrefix(dataUrl: string): string {
  const at = dataUrl.indexOf('base64,');
  return at >= 0 ? dataUrl.slice(at + 'base64,'.length) : dataUrl;
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('unexpected reader result'));
      }
    };
    reader.onerror = () => {
      reject(new Error('read failed'));
    };
    reader.readAsDataURL(file);
  });
}

export function CreateTicketForm(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { user } = useSession();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [ticketType, setTicketType] = useState<TicketType>('billing');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState<readonly PendingAttachment[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);

  const createTicket = useCreateTicket();
  const trimmed = description.trim();

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (trimmed.length === 0) {
      setValidationError(t('tickets.create.error.description_required'));
      return;
    }
    if (trimmed.length > TICKET_TEXT_MAX_LENGTH) {
      setValidationError(
        t('tickets.create.error.description_too_long', { max: TICKET_TEXT_MAX_LENGTH }),
      );
      return;
    }
    setValidationError(null);
    const hadAttachments = attachments.length > 0;
    createTicket.mutate(
      {
        type: ticketType,
        description: trimmed,
        // Always an array, empty when none — the shape the shipped
        // desktop client sends and the server therefore expects.
        attachments: attachments.map((attachment) => ({
          filename: attachment.filename,
          contentType: attachment.contentType,
          dataBase64: attachment.dataBase64,
        })),
      },
      {
        onSuccess: () => {
          setDescription('');
          setAttachments([]);
          toast.success(t('tickets.create.toast.submitted'));
        },
        onError: (error) => {
          toast.error(describeCreateTicketError(error, hadAttachments, t));
        },
      },
    );
  }

  async function handleFiles(fileList: FileList | null): Promise<void> {
    if (fileList === null || fileList.length === 0) {
      return;
    }
    const incoming: PendingAttachment[] = [];
    for (const file of Array.from(fileList)) {
      if (attachments.length + incoming.length >= TICKET_MAX_ATTACHMENTS) {
        toast.error(t('tickets.create.error.too_many_images', { max: TICKET_MAX_ATTACHMENTS }));
        break;
      }
      if (!TICKET_ATTACHMENT_CONTENT_TYPES.includes(file.type)) {
        toast.error(t('tickets.create.error.image_type'));
        continue;
      }
      if (file.size > TICKET_ATTACHMENT_MAX_BYTES) {
        toast.error(t('tickets.create.error.image_size'));
        continue;
      }
      try {
        const dataUrl = await readFileAsDataUrl(file);
        incoming.push({
          id: `${file.name}-${String(file.size)}-${String(Date.now())}-${String(incoming.length)}`,
          filename: file.name,
          contentType: file.type,
          dataBase64: stripDataUrlPrefix(dataUrl),
        });
      } catch {
        toast.error(t('tickets.create.error.image_read', { filename: file.name }));
      }
    }
    if (incoming.length > 0) {
      setAttachments((current) => [...current, ...incoming].slice(0, TICKET_MAX_ATTACHMENTS));
    }
  }

  return (
    <Panel caption={t('tickets.create.caption')} padded testId="tickets-create">
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label={t('tickets.create.type')} fieldId="tickets-create-type">
            <select
              id="tickets-create-type"
              value={ticketType}
              onChange={(event) => {
                const next = TICKET_TYPES.find((type) => type === event.currentTarget.value);
                if (next !== undefined) {
                  setTicketType(next);
                }
              }}
              data-testid="tickets-create-type"
              className={SELECT_CLASS}
            >
              {TICKET_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(TICKET_TYPE_LABEL_KEYS[type])}
                </option>
              ))}
            </select>
          </FormField>
          <FormField
            label={t('tickets.create.email')}
            fieldId="tickets-create-email"
            hint={t('tickets.create.email_hint')}
          >
            <Input
              id="tickets-create-email"
              value={user?.email ?? ''}
              readOnly
              data-testid="tickets-create-email"
              className="text-ink-low"
            />
          </FormField>
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <label htmlFor="tickets-create-description" className="text-label text-ink-mid">
              {t('tickets.create.description')}
              <span aria-hidden="true" className="ml-0.5 text-bad">
                *
              </span>
            </label>
            <span
              className={cx(
                'font-mono text-mono-label tabular-nums',
                description.length >= TICKET_TEXT_MAX_LENGTH ? 'text-bad' : 'text-ink-low',
              )}
              data-testid="tickets-create-counter"
            >
              {description.length}/{TICKET_TEXT_MAX_LENGTH}
            </span>
          </div>
          <Textarea
            id="tickets-create-description"
            value={description}
            rows={5}
            maxLength={TICKET_TEXT_MAX_LENGTH}
            placeholder={t('tickets.create.description_placeholder')}
            aria-describedby={
              validationError !== null ? 'tickets-create-description-error' : undefined
            }
            invalid={validationError !== null}
            data-testid="tickets-create-description"
            onChange={(event) => {
              setDescription(event.currentTarget.value);
              setValidationError(null);
            }}
          />
          {validationError !== null ? (
            <p
              id="tickets-create-description-error"
              role="alert"
              className="m-0 text-label text-bad"
              data-testid="tickets-create-error"
            >
              {validationError}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={TICKET_ATTACHMENT_CONTENT_TYPES.join(',')}
            className="hidden"
            data-testid="tickets-create-attach-input"
            onChange={(event) => {
              void handleFiles(event.currentTarget.files);
              event.currentTarget.value = '';
            }}
          />
          <div className="flex flex-wrap items-center gap-2">
            {attachments.length < TICKET_MAX_ATTACHMENTS ? (
              <Button
                variant="secondary"
                size="sm"
                data-testid="tickets-create-attach"
                onClick={() => fileInputRef.current?.click()}
              >
                <ImagePlus aria-hidden="true" className="h-3.5 w-3.5" />
                {t('tickets.create.add_image')}
              </Button>
            ) : null}
            {attachments.map((attachment) => (
              <span
                key={attachment.id}
                data-testid="tickets-create-attachment"
                className="inline-flex h-7 max-w-[220px] items-center gap-1.5 rounded border border-line bg-well pl-2 pr-1 text-caption text-ink-mid"
              >
                <Paperclip aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-ink-low" />
                <span className="truncate" title={attachment.filename}>
                  {attachment.filename}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('tickets.create.remove_image', { filename: attachment.filename })}
                  className="h-5 px-1"
                  onClick={() =>
                    setAttachments((current) => current.filter((item) => item.id !== attachment.id))
                  }
                >
                  <X aria-hidden="true" className="h-3 w-3" />
                </Button>
              </span>
            ))}
          </div>
          <p className="m-0 text-label text-ink-low">
            {t('tickets.create.attach_hint', { max: TICKET_MAX_ATTACHMENTS })}
          </p>
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            loading={createTicket.isPending}
            disabled={trimmed.length === 0}
            data-testid="tickets-create-submit"
          >
            <Send aria-hidden="true" className="h-3.5 w-3.5" />
            {t('tickets.create.submit')}
          </Button>
        </div>
      </form>
    </Panel>
  );
}
